/**
 * Phase 7 chunk 2C — compass-direction math for the sky-view HUD strip.
 *
 * Pure functions, Cesium-free, so Vitest can cover them directly.
 *
 * Convention:
 *  - Heading is degrees clockwise from north, normalized to [0, 360).
 *  - The 8 directions partition the circle into 45° segments centered on
 *    each cardinal/intercardinal bearing — so a heading of 22.5° maps to
 *    NE (the boundary between N and NE is at 22.5°, and we round up).
 *  - To prevent jitter at the segment boundaries when the camera bobs
 *    slightly, callers can wrap reads in a hysteresis layer; the pure
 *    function below is unsmoothed.
 */

/** The 8 directions in clockwise order from north. */
export const COMPASS_DIRECTIONS = [
  'N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW',
] as const;

export type CompassDirection = (typeof COMPASS_DIRECTIONS)[number];

/** Bearing (degrees clockwise from north) of each direction's center. */
export const COMPASS_BEARING_DEG: Record<CompassDirection, number> = {
  N: 0,
  NE: 45,
  E: 90,
  SE: 135,
  S: 180,
  SW: 225,
  W: 270,
  NW: 315,
};

/** Normalize an arbitrary degree value into the [0, 360) range. */
export function normalizeHeadingDeg(headingDeg: number): number {
  if (!Number.isFinite(headingDeg)) return 0;
  let h = headingDeg % 360;
  if (h < 0) h += 360;
  return h;
}

/**
 * Map a heading in degrees to the closest cardinal/intercardinal
 * direction. Segments are 45° wide, centered on each bearing — heading
 * 22.5 ° lands on NE (the segment boundary rounds to the higher
 * bearing for consistency with map conventions).
 *
 * Handles negative headings + values ≥ 360 by normalizing first.
 */
export function compassDirectionFromHeading(headingDeg: number): CompassDirection {
  const h = normalizeHeadingDeg(headingDeg);
  // Add half a segment (22.5°) so we round to the nearest 45° bucket.
  const index = Math.floor(((h + 22.5) % 360) / 45);
  return COMPASS_DIRECTIONS[index];
}
